import React, { useEffect, useState } from 'react';

interface ProgressBarProps {
  isGenerating: boolean;
  duration?: number; // 预计完成时间（毫秒）
  onComplete?: () => void;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ 
  isGenerating, 
  duration = 15000, // 默认15秒
  onComplete 
}) => {
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('Preparing to generate...');

  useEffect(() => {
    if (!isGenerating) {
      setProgress(0);
      return;
    }

    let interval: NodeJS.Timeout;
    let startTime = Date.now();
    
    // Progress messages
    const messages = [
      { percent: 0, text: '🔍 Analyzing your request...' },
      { percent: 20, text: '🧠 AI thinking of best names...' },
      { percent: 40, text: '📚 Querying Chinese name database...' },
      { percent: 60, text: '✨ Generating meaningful names...' },
      { percent: 80, text: '🎯 Optimizing name combinations...' },
      { percent: 95, text: '✅ Almost complete...' }
    ];

    interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 98); // 最多到98%
      
      setProgress(newProgress);
      
      // 更新消息
      const currentMessage = messages.reduce((prev, curr) => {
        return newProgress >= curr.percent ? curr : prev;
      });
      setMessage(currentMessage.text);
      
      if (newProgress >= 98) {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating, duration]);

  // Set progress to 100% when generation completes
  useEffect(() => {
    if (!isGenerating && progress > 0) {
      setProgress(100);
      setMessage('✨ Generation complete!');
      setTimeout(() => {
        setProgress(0);
        if (onComplete) onComplete();
      }, 500);
    }
  }, [isGenerating, progress, onComplete]);

  if (!isGenerating && progress === 0) return null;

  return (
    <div className="w-full bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-200">
      <div className="space-y-4">
        {/* 进度文字 */}
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">{message}</span>
          <span className="text-sm font-bold text-blue-600">{Math.round(progress)}%</span>
        </div>
        
        {/* 进度条 */}
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-full rounded-full transition-all duration-300 ease-out relative overflow-hidden"
            style={{ width: `${progress}%` }}
          >
            {/* 动画光效 */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
          </div>
        </div>
        
        {/* Hint text */}
        <p className="text-xs text-gray-500 text-center">
          AI is generating the perfect Chinese names for you, please wait...
        </p>
      </div>
    </div>
  );
};

